import { useState, type ReactNode } from "react";
import type { SettlementBatchSummary } from "../types";
import { money, shortDateTime } from "../payrollPresentation";
import { Button } from "./ui/Button";
import { PayrollSmsButton } from "./PayrollSmsButton";
import { usePlanningBaseline } from "./ui/DesignSystem";
import { PayrollBatchWorkerTotals } from "./PayrollBatchWorkerTotals";

type Props = {
  batches: SettlementBatchSummary[];
  selected: SettlementBatchSummary | null;
  page: number;
  hasNext: boolean;
  disabled: boolean;
  onSelect: (batch: SettlementBatchSummary) => void;
  onPrevious: () => void;
  onNext: () => void;
  onDownload: (batch: SettlementBatchSummary) => void;
  onSms: (batch: SettlementBatchSummary) => void;
  onRename: (batch: SettlementBatchSummary, name: string) => void;
  renderRecords: () => ReactNode;
};

export function PayrollBatchHistory({ batches, selected, page, hasNext, disabled, onSelect, onPrevious, onNext, onDownload, onSms, onRename, renderRecords }: Props) {
  const baseline = usePlanningBaseline();
  const [editing, setEditing] = useState<{ id: string; name: string } | null>(null);
  const commit = (batch: SettlementBatchSummary) => {
    const name = editing?.name.trim();
    if (name && name !== batch.name) onRename(batch, name);
    setEditing(null);
  };
  if (baseline) return <div className="payroll-batch-strip">
    <nav className="payroll-batch-chips" aria-label="정산 묶음 페이지 인디케이터">
      <Button size="compact" disabled={disabled || page === 0} aria-label="이전 정산 묶음 10개" onClick={onPrevious}>‹</Button>
      {batches.map(batch => <button key={batch.id} type="button" className={`ui-chip payroll-batch-chip${selected?.id === batch.id ? " selected" : ""}`}
        aria-current={selected?.id === batch.id ? "page" : undefined} onClick={() => onSelect(batch)} title={batch.name}>{batch.name}</button>)}
      {!batches.length && <span className="payroll-muted">완료된 정산 묶음이 없습니다.</span>}
      <Button size="compact" disabled={disabled || !hasNext} aria-label="다음 정산 묶음 10개" onClick={onNext}>›</Button>
    </nav>
    {selected && <section className="payroll-batch-open" data-testid="settlement-batch-detail">
      <div className="payroll-batch-line"><div className="payroll-batch-name">
        {editing?.id === selected.id ? <input autoFocus className="payroll-batch-rename" value={editing.name} maxLength={100} aria-label="묶음명"
          onChange={event => setEditing({ id: selected.id, name: event.target.value })} onBlur={() => commit(selected)}
          onKeyDown={event => { if (event.key === "Enter") commit(selected); if (event.key === "Escape") setEditing(null); }} />
          : <strong role="button" tabIndex={0} title="눌러서 이름을 고칩니다." onClick={() => setEditing({ id: selected.id, name: selected.name })}
            onKeyDown={event => { if (event.key === "Enter") setEditing({ id: selected.id, name: selected.name }); }}>{selected.name}</strong>}
        <span className="payroll-batch-summary" data-testid="all-settlement-batch"><span className="payroll-status paid">정산 완료</span> · {selected.recordCount.toLocaleString()}건 · {selected.recipientCount.toLocaleString()}명 · <b>{money(selected.payableAmount)}</b> · {shortDateTime(selected.completedAt ?? selected.createdAt)}</span>
      </div><div className="payroll-batch-line-actions"><Button size="compact" onClick={() => onDownload(selected)}>이체 파일 받기</Button><Button size="compact" data-testid="open-settlement-sms" onClick={() => onSms(selected)}>정산 메시지</Button></div></div>
      <PayrollBatchWorkerTotals batchId={selected.id} revision={`${selected.recordCount}:${selected.payableAmount}`} />
      {renderRecords()}
    </section>}
  </div>;
  return <div className="payroll-history">
    <nav className="payroll-history-pages" aria-label="정산 묶음 페이지 인디케이터">
      <Button size="compact" disabled={disabled || page === 0} aria-label="이전 정산 묶음 10개" onClick={onPrevious}>‹</Button>
      <div className="payroll-history-page-items">
        {batches.map(batch => <button key={batch.id} type="button" className="payroll-history-page-item"
          data-selected={selected?.id === batch.id || undefined} aria-current={selected?.id === batch.id ? "page" : undefined}
          onClick={() => onSelect(batch)} title={batch.name}>{batch.name}</button>)}
        {!batches.length && <span className="payroll-muted">완료된 정산 묶음이 없습니다.</span>}
      </div>
      <Button size="compact" disabled={disabled || !hasNext} aria-label="다음 정산 묶음 10개" onClick={onNext}>›</Button>
    </nav>
    {selected && <section className="payroll-history-detail payroll-batch-group" data-testid="settlement-batch-detail">
      <div className="payroll-history-detail-head">
        <div className="payroll-history-detail-title">
          {editing?.id === selected.id
            ? <input autoFocus className="payroll-batch-rename" value={editing.name} maxLength={100} aria-label="묶음명"
                onChange={event => setEditing({ id: selected.id, name: event.target.value })}
                onBlur={() => commit(selected)}
                onKeyDown={event => { if (event.key === "Enter") commit(selected); if (event.key === "Escape") setEditing(null); }} />
            : <><h3>{selected.name}</h3><button type="button" className="payroll-batch-rename-icon" aria-label={`${selected.name} 이름 변경`}
                onClick={() => setEditing({ id: selected.id, name: selected.name })}>✎</button></>}
          <span className="payroll-status paid">정산 완료</span>
        </div>
        <div className="payroll-history-detail-actions">
          <Button size="compact" onClick={() => onDownload(selected)}>CSV</Button>
          <PayrollSmsButton batch={selected} onClick={() => onSms(selected)} />
        </div>
      </div>
      <p className="payroll-history-detail-summary" data-testid="all-settlement-batch">
        <span>{selected.recordCount.toLocaleString()}건</span>
        <span>{selected.recipientCount.toLocaleString()}명</span>
        <span>{money(selected.payableAmount)}</span>
        <span>{shortDateTime(selected.completedAt ?? selected.createdAt)}</span>
      </p>
      <div className="payroll-batch-body"><PayrollBatchWorkerTotals batchId={selected.id} revision={`${selected.recordCount}:${selected.payableAmount}`} />{renderRecords()}</div>
    </section>}
  </div>;
}
